import React, {useEffect, useState} from 'react';
import cn from 'classnames';
import {flushSync} from 'react-dom';
import {useSearchParams} from 'react-router-dom';

import {IStatusPageView} from '../../model/IStatusPageView';
import {resolveOverall, sortMonitors} from '../../lib/statusPageHelper';

import StatusPageHero from './StatusPageHero/StatusPageHero';
import StatusMonitorRow from './StatusMonitorRow/StatusMonitorRow';
import StatusPageFooter from './StatusPageFooter/StatusPageFooter';

import styles from './StatusPageView.module.scss';

interface IStatusPageViewProps {
    data: IStatusPageView,
    onDownloadPdf?: () => void,
    isPdfLoading?: boolean,
    hideActions?: boolean,
}

const StatusPageView: React.FC<IStatusPageViewProps> = ({data, onDownloadPdf, isPdfLoading, hideActions}) => {
    const overall = resolveOverall(data);
    const monitors = sortMonitors(data.monitors ?? []);
    const [searchParams] = useSearchParams();
    const isPdfExport = searchParams.has('pdf');
    const [printMode, setPrintMode] = useState(false);
    const expanded = printMode || isPdfExport;

    useEffect(() => {
        const handleBeforePrint = () => flushSync(() => setPrintMode(true));
        const handleAfterPrint = () => setPrintMode(false);
        window.addEventListener('beforeprint', handleBeforePrint);
        window.addEventListener('afterprint', handleAfterPrint);
        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    useEffect(() => {
        if (!printMode) return undefined;
        const timer = setTimeout(() => window.print(), 450);
        return () => clearTimeout(timer);
    }, [printMode]);

    useEffect(() => {
        window.PDF_READY = false;
        if (!isPdfExport) return undefined;
        let innerRaf = 0;
        const outerRaf = requestAnimationFrame(() => {
            innerRaf = requestAnimationFrame(() => {
                window.PDF_READY = true;
            });
        });
        return () => {
            cancelAnimationFrame(outerRaf);
            cancelAnimationFrame(innerRaf);
        };
    }, [isPdfExport]);

    return (
        <div className={styles.page}>
            <StatusPageHero
                name={data.name}
                description={data.description}
                overall={overall}
                lastCheckedAt={data.last_checked_at}
                onDownloadPdf={onDownloadPdf}
                isPdfLoading={isPdfLoading}
                onPrint={() => setPrintMode(true)}
                hideActions={hideActions}
            />

            <main className={cn(styles.content, {[styles.noBanner]: data.hide_banner})}>
                <div className={styles.list}>
                    {monitors.map((monitor, index) => (
                        <StatusMonitorRow
                            key={`${index}-${monitor.name}`}
                            monitor={monitor}
                            defaultExpanded={false}
                            forceExpanded={expanded}
                        />
                    ))}
                </div>
                {
                    (data && !data.hide_banner) && <StatusPageFooter/>
                }
            </main>
        </div>
    );
};

export default StatusPageView;
